// --- React · 라우팅 ---
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 ---
import { useTheme } from '@emotion/react';
import type { AppTheme } from '@/theme/theme';
import { createAuthStyles } from '@/components/auth/authStyles';
import registerImage from '/images/register.png';

// --- 공통 UI 컴포넌트 ---
import { Input } from '@/components/ui/common/input/Input';
import { LanguageTabs } from '@/components/ui/common/LanguageTabs';
import { Button } from '@/components/ui';

// --- API ---
import { apiHelpers } from '@/api/core';

// --- 유효성 검사 · 에러 파싱 ---
import { parseApiError } from '@/utils/errors';

// --- 스토어 ---
import { useNotificationStore } from '@/store/notificationStore';

// --- 타입 ---
import type {
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
} from '@packages/types/auth/dto';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 컴포넌트 ====================

const ForgotPassword: React.FC = () => {
  const theme = useTheme() as AppTheme;
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotificationStore();

  // ==================== 상태 관리 ====================

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // ==================== 핸들러 ====================

  /** 언어 변경 */
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  /**
   * 비밀번호 재설정 이메일 발송.
   * 흐름:
   *   1) 이메일 주소와 현재 언어를 포함해 API 호출
   *   2) 성공 시 발송 완료 화면으로 전환
   *   3) 실패 시 에러 알림 표시
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const requestData: RequestPasswordResetRequest = {
        email,
        language: i18n.language.split('-')[0],
      };
      const response = await apiHelpers.post<RequestPasswordResetResponse>(
        'auth.requestPasswordReset',
        requestData
      );

      if (response.data?.success) {
        setEmailSent(true);
        showSuccess('auth:password_reset_email_sent', 'auth:check_your_email_for_reset_link');
      }
    } catch (error: unknown) {
      const e = parseApiError(error);
      showError(e.title, e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== 렌더링 ====================

  const styles = createAuthStyles(registerImage, theme);

  // 이메일 발송 완료 화면
  if (emailSent) {
    return (
      <div css={styles.container}>
        {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
        <div css={styles.leftSection}>
          <div css={styles.leftOverlay} />
          <div css={styles.leftContent}>
            <div css={styles.leftText}>
              <img src="/images/bellcloud.svg" alt="BellCloud Logo" css={styles.logo} />
            </div>
          </div>
        </div>

        {/* 오른쪽 섹션 - 발송 완료 안내 */}
        <div css={styles.rightSection}>
          <div css={styles.formContainer}>
            {/* 타이틀 + 언어 선택 */}
            <div css={styles.header}>
              <div>
                <h2 css={styles.formTitle}>{t(i18nKeys.auth.email_sent)}</h2>
                <div css={styles.formTitleLine} />
              </div>

              <div css={styles.headerLanguageTabs}>
                <LanguageTabs
                  currentLanguage={i18n.language}
                  onLanguageChange={handleLanguageChange}
                />
              </div>
            </div>

            {/* 발송 완료 콘텐츠 */}
            <div css={styles.contentAreaCentered}>
              <div css={styles.iconLarge}>✉️</div>
              <p css={styles.messageTextSecondary}>
                {t(i18nKeys.auth.password_reset_email_sent_description)}
              </p>
              <Button
                type="button"
                variant="primary"
                width="100%"
                onClick={() => navigate('/login')}
              >
                {t(i18nKeys.auth.back_to_login)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 기본 화면 — 이메일 입력 폼
  return (
    <div css={styles.container}>
      {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
      <div css={styles.leftSection}>
        <div css={styles.leftOverlay} />
        <div css={styles.leftContent}>
          <div css={styles.leftText}>
            <img src="/images/bellcloud.svg" alt="BellCloud Logo" css={styles.logo} />
          </div>
        </div>
      </div>

      {/* 오른쪽 섹션 - 비밀번호 재설정 요청 폼 */}
      <div css={styles.rightSection}>
        <div css={styles.formContainer}>
          {/* 타이틀 + 언어 선택 */}
          <div css={styles.header}>
            <div>
              <h2 css={styles.formTitle}>{t(i18nKeys.auth.forgot_password)}</h2>
              <div css={styles.formTitleLine} />
            </div>

            <div css={styles.headerLanguageTabs}>
              <LanguageTabs
                currentLanguage={i18n.language}
                onLanguageChange={handleLanguageChange}
              />
            </div>
          </div>

          <p css={styles.description}>{t(i18nKeys.auth.enter_email_for_password_reset)}</p>

          {/* 이메일 입력 폼 */}
          <form css={styles.form} onSubmit={handleSubmit}>
            <div css={styles.fieldGroup}>
              {/* 이메일 필드 */}
              <div css={styles.field}>
                <p>{t(i18nKeys.auth.e_mail)} *</p>
                <Input
                  type="email"
                  name="email"
                  size="md"
                  value={email}
                  onChange={setEmail}
                  placeholder={t(i18nKeys.auth.e_mail)}
                  fullWidth
                />
              </div>
            </div>

            {/* 재설정 링크 발송 버튼 */}
            <Button type="submit" variant="primary" disabled={isLoading} width="100%">
              {isLoading ? t(i18nKeys.auth.sending) : t(i18nKeys.auth.send_reset_link)}
            </Button>
          </form>

          {/* 하단 링크 (로그인 페이지로 이동) */}
          <div css={styles.bottomLinks}>
            <p css={styles.linkText}>
              {t(i18nKeys.auth.remember_password)}{' '}
              <Link to="/login" css={styles.link}>
                {t(i18nKeys.auth.back_to_login)}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
